import React, { useCallback, useMemo, useState } from "react";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import { Team } from "domains/teams/interfaces/Team";
import { formatTeamName } from "domains/teams/utils";
import Avatar from "domains/ui/components/Avatar";
import ButtonWithModal from "domains/ui/components/ButtonWithModal";
import Icon from "domains/ui/components/Icon";
import MenuItem from "domains/ui/components/Menu/MenuItem";
import { PremiumButtonWrapper } from "domains/ui/components/PremiumBadge";
import ScenarioInput from "domains/ui/components/ScenarioInput";
import { useHandleApiError } from "infra/api/error";
import {
  GetModelsByModelIdApiResponse,
  usePostModelsTransferByModelIdMutation,
} from "infra/api/generated/api";

import { Box, HStack, Text, useDisclosure, VStack } from "@chakra-ui/react";

const MAX_MODEL_NAME_LENGTH = 30;

function formatModelName(name: string) {
  const formattedName = name.replace(/ /g, "\u00A0");
  return formattedName.length > MAX_MODEL_NAME_LENGTH
    ? `${formattedName.slice(0, MAX_MODEL_NAME_LENGTH)}...`
    : formattedName;
}

export interface TransferModalProps {
  model: GetModelsByModelIdApiResponse["model"];
  onClick: () => void;
}

export default function TransferModal({ model, onClick }: TransferModalProps) {
  const { teams, selectedTeam, selectedProject } = useTeamContext();
  const [transferTo, setTransferTo] = useState<string>("");
  const [transferModelMutation] = usePostModelsTransferByModelIdMutation();
  const [isTransferLoading, setIsTransferLoading] = useState(false);
  const { successToast } = useScenarioToast();
  const handleApiError = useHandleApiError();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const isUnableToTransferModel = useMemo(
    () =>
      selectedTeam.isFreePlan ||
      selectedTeam.plan === "cu-creator" ||
      selectedTeam.plan === "cu-pro",
    [selectedTeam.isFreePlan, selectedTeam.plan]
  );

  const projectSelectOptions = useMemo(
    () =>
      teams.reduce<(Team["projects"][number] & { teamName: string })[]>(
        (memo, team) => {
          if (!team.projects.length) return memo;
          const projects = team.projects.filter(
            (project) => project.id !== selectedProject.id
          );
          if (!projects.length) return memo;
          projects.forEach((project) => {
            memo.push({ ...project, teamName: team.name });
          });
          return memo;
        },
        []
      ),
    [teams, selectedProject.id]
  );

  // ----------------------------------

  const handleConfirmTransfer = useCallback(async () => {
    try {
      setIsTransferLoading(true);
      await transferModelMutation({
        modelId: model.id,
        projectId: selectedProject.id,
        body: {
          destinationProjectId: transferTo,
        },
      }).unwrap();
      successToast({
        title: "Your model has been transferred",
      });
    } catch (error) {
      handleApiError(error, "There was an error transferring the model");
    } finally {
      setIsTransferLoading(false);
    }
  }, [
    transferModelMutation,
    model.id,
    selectedProject.id,
    transferTo,
    successToast,
    handleApiError,
  ]);

  const handleProjectSelectChange = useCallback(
    (projectId: string) => {
      setTransferTo(projectId);
    },
    [setTransferTo]
  );

  const handleOpen = useCallback(() => {
    setTransferTo("");
    onClose();
    onClick();
  }, [onClick, onClose, setTransferTo]);

  // ----------------------------------

  return (
    <PremiumButtonWrapper
      shouldShowWrapper={isUnableToTransferModel}
      shouldShowBorder={false}
    >
      <MenuItem
        as={ButtonWithModal}
        isModalConfirmButtonDisabled={!transferTo}
        iconId="Ui/ArrowRight"
        modalHeader={<Box />}
        modalBody={
          <VStack align="stretch" w="full" onClick={onClose} spacing={8}>
            {!isOpen && (
              <VStack align="stretch">
                <Icon
                  id="Ui/Share/Outline"
                  color="primary.500"
                  filter={"drop-shadow(0px 0px 25px #0088CC)"}
                  mb={5}
                />
                <Text mb={2} size="title.sm">
                  {`You're about to transfer the following model to another project: ${formatModelName(
                    model?.name ?? ""
                  )}`}
                </Text>
                <Text align="start" color="textSecondary">
                  A copy of the model will be created and transferred, leaving
                  the original in your project.
                  {model.type.includes("-composition") &&
                    " By transferring this composition, you'll also transfer copies of its component models."}
                </Text>
              </VStack>
            )}

            <VStack align="stretch" w="full">
              <Text
                align="start"
                mb={2}
                color="textSecondary"
                size="body.bold.md"
              >
                Project ID
              </Text>

              <VStack align="stretch">
                <ScenarioInput
                  setValue={setTransferTo}
                  value={transferTo}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onOpen();
                  }}
                />

                <Text
                  align="start"
                  color="textSecondary"
                  size="body.sm"
                >{`Find project ID in Projects > Manage Project`}</Text>
              </VStack>

              {isOpen && (
                <VStack
                  overflowY="auto"
                  h="210px"
                  p={1}
                  pt={0.5}
                  borderWidth={1}
                  borderColor="border.500"
                  borderRadius="lg"
                  bgColor="backgroundSecondary.500"
                  spacing={0}
                >
                  {projectSelectOptions.map((project) => (
                    <MenuItem
                      key={project.id}
                      onClick={() => handleProjectSelectChange(project.id)}
                    >
                      <HStack>
                        <Avatar
                          w="24px"
                          h="24px"
                          borderRadius="md"
                          label={formatTeamName(project.teamName)}
                          size="md"
                          singleLetter
                        />
                        <Text color="textTertiary">{project.teamName}</Text>
                        <Text color="textTertiary">{">"}</Text>
                        <Text>{project.name}</Text>
                      </HStack>
                    </MenuItem>
                  ))}
                </VStack>
              )}
            </VStack>
          </VStack>
        }
        modalConfirmMessage="Transfer model"
        modalShowCancelButton={false}
        onConfirm={handleConfirmTransfer}
        onOpen={handleOpen}
        isModalConfirmButtonLoading={isTransferLoading}
        isDisabled={isUnableToTransferModel}
        tooltip={
          isUnableToTransferModel
            ? "Upgrade your plan to transfer model to another project"
            : undefined
        }
        text="Transfer"
        modalContentProps={{
          overflowY: "auto",
          w: "full",
          onClick: onClose,
        }}
      />
    </PremiumButtonWrapper>
  );
}
